import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  FileText, Send, Inbox, Loader2, AlertTriangle, CheckCircle2, XCircle,
  CalendarClock, RefreshCw, Plus, ChevronLeft, Clock,
} from 'lucide-react';

// DCS ATS — Submissions.
// Three views behind three tabs (spec Part 4):
//   * New Submission — the composer: requirement + candidates -> queue the mail.
//   * Submissions    — the outcome ledger (Awaiting / Closed) with per-candidate
//                      client decisions.
//   * Outbox         — the send queue (Failed / Queued / Sending / Sent), retry.
//
// THE RULE this module finally enforces (which JM never did): creating a
// submission moves the candidate to submitted_to_client, and a client decision
// moves them to shortlisted / interview / selected / rejected. The stage is set
// by the backend on the event itself — this page just shows it and records the
// verdict.

const DECISIONS = [
  { id: 'shortlisted', label: 'Shortlist', icon: CheckCircle2, cls: 'bg-emerald-600' },
  { id: 'interview', label: 'Interview', icon: CalendarClock, cls: 'bg-indigo-600' },
  { id: 'selected', label: 'Select', icon: CheckCircle2, cls: 'bg-green-700' },
  { id: 'rejected', label: 'Reject', icon: XCircle, cls: 'bg-red-600' },
];

const STATUS_CLS = {
  sent: 'bg-emerald-100 text-emerald-700', queued: 'bg-slate-100 text-slate-600',
  sending: 'bg-amber-100 text-amber-700', failed: 'bg-red-100 text-red-700',
};

const fmt = (v) => (v ? new Date(v).toLocaleString('en-IN',
  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export default function SubmissionsPage() {
  const [tab, setTab] = useState('ledger');            // ledger | new | outbox
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto max-md:pb-[calc(1rem+env(safe-area-inset-bottom))]" data-testid="submissions-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 max-md:flex-col max-md:items-stretch">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-700" /> Submissions
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Send shortlists to clients and track their verdicts. Sending a submission
            moves the candidate to <b>submitted_to_client</b>; a client decision moves
            the stage on — automatically.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 max-md:w-full">
          {[['ledger', 'Submissions'], ['new', 'New Submission'], ['outbox', 'Outbox']].map(([id, label]) => (
            <button key={id} onClick={() => { setTab(id); setError(''); }}
                    className={`text-xs px-3 py-1.5 rounded-md max-md:flex-1 max-md:min-h-[44px] ${
                      tab === id ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600'}`}
                    data-testid={`tab-${id}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-3 text-xs text-red-600 bg-red-50 rounded-lg">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /><span className="break-all">{error}</span>
        </div>
      )}

      {tab === 'ledger' && <Ledger setError={setError} busy={busy} setBusy={setBusy} />}
      {tab === 'new' && <Composer setError={setError} busy={busy} setBusy={setBusy}
                                  onDone={() => setTab('outbox')} />}
      {tab === 'outbox' && <Outbox setError={setError} busy={busy} setBusy={setBusy} />}
    </div>
  );
}

// --------------------------------------------------------------------------
// Submissions ledger — Awaiting / Closed, with per-candidate decisions.
// --------------------------------------------------------------------------

function Ledger({ setError, busy, setBusy }) {
  const [sub, setSub] = useState('awaiting');
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);       // {submission, decisions}
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/submissions?tab=${sub}`);
      setRows(r.data.submissions || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setLoading(false);
  }, [sub, setError]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setBusy(`d-${id}`);
    try {
      const r = await api.get(`/submissions/${id}`);
      setDetail(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  const decide = async (candidateId, decision) => {
    setBusy(`dec-${candidateId}-${decision}`);
    try {
      await api.post(`/submissions/${detail.submission.id}/decision`,
        { candidate_id: candidateId, decision });
      await openDetail(detail.submission.id);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  if (loading) return <Spin />;

  if (detail) {
    const s = detail.submission;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <button onClick={() => setDetail(null)}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-3">
          <ChevronLeft className="w-4 h-4" /> Back to ledger
        </button>
        <h2 className="text-base font-semibold text-slate-900">{s.subject}</h2>
        <p className="text-xs text-slate-500 mb-1">To {s.to_email} · sent {fmt(s.sent_at)}</p>
        {s.reply_to && (
          <p className="text-xs text-slate-400 mb-4">
            Replies route back via <code>{s.reply_to}</code>
          </p>
        )}
        <div className="space-y-2">
          {(detail.decisions || []).map((d) => (
            <div key={d.candidate_id}
                 className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg px-3 py-2 max-md:flex-col max-md:items-stretch max-md:gap-2">
              <div className="min-w-0">
                <p className="text-sm text-slate-800 truncate">{d.candidate_id}</p>
                <p className="text-xs text-slate-400">
                  Verdict: <b className={d.decision === 'pending' ? 'text-slate-500' : 'text-slate-800'}>{d.decision}</b>
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 max-md:w-full max-md:gap-2">
                {DECISIONS.map((dec) => {
                  const Icon = dec.icon;
                  return (
                    <button key={dec.id} onClick={() => decide(d.candidate_id, dec.id)}
                            disabled={!!busy}
                            className={`text-xs px-2 py-1 rounded text-white disabled:opacity-40 max-md:flex-1 max-md:min-h-[44px] max-md:inline-flex max-md:items-center max-md:justify-center ${dec.cls}`}
                            title={dec.label}>
                      {busy === `dec-${d.candidate_id}-${dec.id}`
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Icon className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit mb-3 max-md:w-full">
        {['awaiting', 'closed'].map((t) => (
          <button key={t} onClick={() => setSub(t)}
                  className={`text-xs px-3 py-1.5 rounded-md capitalize max-md:flex-1 max-md:min-h-[44px] ${
                    sub === t ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600'}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">Nothing here.</div>}
        {rows.map((s) => (
          <button key={s.id} onClick={() => openDetail(s.id)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 max-md:flex-wrap max-md:gap-y-1.5 max-md:min-h-[44px]"
                  data-testid="submission-row">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{s.subject}</p>
              <p className="text-xs text-slate-400 truncate">
                {s.to_email} · {(s.candidate_ids || []).length} candidate(s)
              </p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[s.status] || 'bg-slate-100 text-slate-500'}`}>
              {s.status}
            </span>
            <span className="text-xs text-slate-400 w-28 text-right max-md:w-auto max-md:text-left">{fmt(s.sent_at || s.created_at)}</span>
          </button>
        ))}
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// New Submission composer.
// --------------------------------------------------------------------------

function Composer({ setError, busy, setBusy, onDone }) {
  const [requirementId, setRequirementId] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState([]);
  const [toEmail, setToEmail] = useState('');
  const [intro, setIntro] = useState('');
  const [result, setResult] = useState(null);
  // Per-candidate client note. Prints under the signature of the submission
  // mail as "Name: <note>", and is handed to the dossier generator as the
  // consultant's first-hand read of the candidate. Keyed by requirement +
  // candidate, so a note written for one role never follows them to another.
  const [notes, setNotes] = useState({});
  const [noteSaved, setNoteSaved] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/requirements?status=open&limit=100');
        setRequirements(r.data.requirements || r.data || []);
      } catch (_) { /* requirements API shape varies; composer still works via manual ids */ }
    })();
  }, []);

  const loadCandidates = async (rid) => {
    setRequirementId(rid);
    setPicked([]);
    try {
      const r = await api.get(`/requirements/${rid}/candidates`);
      setCandidates(r.data.candidates || r.data || []);
    } catch (_) {
      setCandidates([]);
    }
    try {
      const n = await api.get(`/submissions/notes/list?requirement_id=${rid}`);
      setNotes(n.data.notes || {});
    } catch (_) { setNotes({}); }
  };

  const saveNote = async (candidateId) => {
    try {
      await api.post('/submissions/notes/set', {
        requirement_id: requirementId || null,
        candidate_id: candidateId,
        note: notes[candidateId] || '',
      });
      setNoteSaved((m) => ({ ...m, [candidateId]: true }));
      setTimeout(() => setNoteSaved((m) => ({ ...m, [candidateId]: false })), 1600);
    } catch (_) { /* the note is not worth failing a submission over */ }
  };

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (picked.length === 0) { setError('Pick at least one candidate.'); return; }
    setBusy('create');
    setError('');
    try {
      const r = await api.post('/submissions', {
        requirement_id: requirementId || null,
        candidate_ids: picked,
        to_email: toEmail || null,
        intro: intro || null,
      });
      setResult(r.data);
      if (r.data.status === 'sent' && onDone) setTimeout(onDone, 1200);
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  if (result) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 text-center space-y-3">
        {result.status === 'sent'
          ? <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
          : <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />}
        <p className="text-sm text-slate-800 font-medium">
          {result.status === 'sent' ? 'Submission sent.'
            : result.status === 'queued'
              ? 'Queued — building each candidate\u2019s dossier PDF, then sending.'
              : `Queued — send status: ${result.status}`}
        </p>
        <p className="text-xs text-slate-500">
          {(result.candidate_ids || []).length} candidate(s) moved to <b>submitted_to_client</b>
          {result.stage_moves?.moved?.length
            ? ` (${result.stage_moves.moved.length} advanced from new/screening).`
            : '.'}
        </p>
        {result.status === 'queued' && (
          <p className="text-xs text-slate-500">
            The dossier PDFs take a minute or two to build. Watch the <b>Outbox</b> tab —
            the status moves to <b>sent</b> once the mail is away.
          </p>
        )}
        {result.status === 'failed' && (
          <p className="text-xs text-red-600">Send failed: {result.error}. Retry it from the Outbox.</p>
        )}
        <button onClick={() => { setResult(null); setPicked([]); }}
                className="text-xs px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200">
          New submission
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <div>
        <label className="text-xs text-slate-500">Requirement</label>
        <select value={requirementId} onChange={(e) => loadCandidates(e.target.value)}
                className="w-full mt-1 text-sm border border-slate-200 rounded-lg p-2"
                data-testid="composer-requirement">
          <option value="">Select a requirement…</option>
          {requirements.map((r) => (
            <option key={r.id} value={r.id}>{r.position_title} — {r.client_name || r.client_id}</option>
          ))}
        </select>
      </div>

      {candidates.length > 0 && (
        <div>
          <label className="text-xs text-slate-500">Candidates ({picked.length} selected)</label>
          <div className="mt-1 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {candidates.map((c) => (
              <div key={c.id}>
                <label className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} />
                  <span className="flex-1 truncate">{c.full_name || c.name || c.id}</span>
                  <span className="text-xs text-slate-400">{c.status}</span>
                </label>
                {picked.includes(c.id) && (
                  <div className="px-3 pb-3 pt-1 bg-amber-50/40">
                    <label className="text-[11px] text-slate-500">
                      Note to the client about {(c.full_name || c.name || 'this candidate').split(' ')[0]}
                      <span className="text-slate-400"> — prints in the mail as
                        &ldquo;{(c.full_name || c.name || 'Name').split(' ')[0]}: …&rdquo; and feeds their dossier</span>
                    </label>
                    <textarea
                      rows={2}
                      value={notes[c.id] || ''}
                      onChange={(e) => setNotes((m) => ({ ...m, [c.id]: e.target.value }))}
                      onBlur={() => saveNote(c.id)}
                      placeholder="e.g. Candidate needs a hike of 30%. Open to relocating. Available for interviews after 6pm."
                      className="w-full mt-1 text-sm border border-amber-200 rounded-lg p-2 bg-white
                                 focus:outline-none focus:ring-1 focus:ring-amber-300"
                      data-testid={`note-${c.id}`} />
                    {noteSaved[c.id] && (
                      <span className="text-[11px] text-emerald-600">Saved</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-xs text-slate-500">Client email (optional — defaults to the client on file)</label>
        <input value={toEmail} onChange={(e) => setToEmail(e.target.value)}
               placeholder="hiring@client.com"
               className="w-full mt-1 text-sm border border-slate-200 rounded-lg p-2" />
      </div>
      <div>
        <label className="text-xs text-slate-500">Intro note (optional)</label>
        <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3}
                  className="w-full mt-1 text-sm border border-slate-200 rounded-lg p-2" />
      </div>

      <div className="flex items-center justify-end">
        <button onClick={submit} disabled={busy === 'create' || picked.length === 0}
                className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
                data-testid="composer-send">
          {busy === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send submission
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Outbox — the send queue.
// --------------------------------------------------------------------------

function Outbox({ setError, busy, setBusy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/submissions/outbox');
      setData(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setLoading(false);
  }, [setError]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setBusy(`retry-${id}`);
    try {
      await api.post(`/submissions/${id}/retry`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
    setBusy('');
  };

  if (loading) return <Spin />;

  const groups = [
    ['failed', 'Failed', XCircle, 'text-red-600'],
    ['queued', 'Queued', Clock, 'text-slate-500'],
    ['sending', 'Sending', Loader2, 'text-amber-600'],
    ['sent', 'Sent', CheckCircle2, 'text-emerald-600'],
  ];

  return (
    <div className="space-y-4">
      {groups.map(([key, label, Icon, cls]) => {
        const rows = (data && data[key]) || [];
        return (
          <div key={key} className="bg-white border border-slate-200 rounded-xl">
            <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2">
              <Icon className={`w-4 h-4 ${cls}`} />
              <h3 className="text-sm font-medium text-slate-800">{label}</h3>
              <span className="text-xs text-slate-400">({rows.length})</span>
            </div>
            <div className="divide-y divide-slate-100">
              {rows.length === 0 && <div className="px-4 py-3 text-xs text-slate-400">None.</div>}
              {rows.map((s) => (
                <div key={s.id} className="px-4 py-3 flex items-center gap-3 max-md:flex-wrap max-md:gap-y-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate">{s.subject}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {s.to_email} · attempt {s.attempts || 0}{s.error ? ` · ${s.error}` : ''}
                    </p>
                  </div>
                  {key === 'failed' && (
                    <button onClick={() => retry(s.id)} disabled={!!busy}
                            className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40 max-md:min-h-[44px] max-md:px-4">
                      {busy === `retry-${s.id}`
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <RefreshCw className="w-3 h-3 inline -mt-0.5 mr-1" />}
                      Retry
                    </button>
                  )}
                  <span className="text-xs text-slate-400 w-28 text-right max-md:w-auto max-md:text-left">{fmt(s.updated_at)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Spin() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
    </div>
  );
}
